import { useState, useEffect, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRegeneratePDF } from "@/hooks/useRegeneratePDF";
import { useToast } from "@/hooks/use-toast";
import {
  FileText,
  Download,
  RefreshCw,
  Send,
  Activity,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import StateBadge from "@/components/ui/stateBadge";
import { ordersApi } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { isErpDirectIntegrationEnabled } from "@/features/erpDirectIntegrationFeature";
import { ErpIntegrationSheet } from "@/components/ErpIntegration/ErpIntegrationSheet";

type SortKey = "orderNumber" | "clientName" | "clientCode" | "totalAmount" | "status" | "createdAt";
type SortDir = "asc" | "desc";

const PAGE_SIZES = [ 10, 25, 50, 100 ];

// Cada botão de ação ocupa uma coluna de largura fixa, para os botões ficarem alinhados de uma linha para outra.
const ACTION_SLOT = "w-[124px]";

export default function OrderHistory() {
  const { regeneratePDF } = useRegeneratePDF();
  const { toast } = useToast();
  const [ regeneratingId, setRegeneratingId ] = useState<string | null>( null );
  const [ resendingWebhookId, setResendingWebhookId ] = useState<string | null>( null );
  const [ integrationOrder, setIntegrationOrder ] = useState<{ id: string; orderNumber: string } | null>( null );
  const [ webhookCooldowns, setWebhookCooldowns ] = useState<Record<string, number>>( {} );

  // Filtros, ordenação e paginação (feitos no navegador: a API devolve o histórico inteiro de uma vez).
  const [ search, setSearch ] = useState( "" );
  const [ statusFilter, setStatusFilter ] = useState( "all" );
  const [ dateFrom, setDateFrom ] = useState( "" );
  const [ dateTo, setDateTo ] = useState( "" );
  const [ sortKey, setSortKey ] = useState<SortKey>( "createdAt" );
  const [ sortDir, setSortDir ] = useState<SortDir>( "desc" );
  const [ page, setPage ] = useState( 1 );
  const [ pageSize, setPageSize ] = useState( 25 );

  useEffect( () => {
    const now = new Date().getTime();
    try {
      const storedCooldowns = JSON.parse(
        localStorage.getItem( "webhookCooldowns" ) || "{}"
      );
      const activeCooldowns: Record<string, number> = {};

      for ( const orderId in storedCooldowns ) {
        if ( storedCooldowns[ orderId ] > now ) {
          activeCooldowns[ orderId ] = storedCooldowns[ orderId ];
        }
      }

      setWebhookCooldowns( activeCooldowns );
      localStorage.setItem( "webhookCooldowns", JSON.stringify( activeCooldowns ) );
    } catch ( error ) {
      console.error( "Failed to parse webhook cooldowns from localStorage", error );
      localStorage.removeItem( "webhookCooldowns" );
    }
  }, [] );

  // Buscar todos os pedidos
  const { data: orders, isLoading: isLoadingOrders, refetch } = useQuery( {
    queryKey: [ "orders", "all" ],
    queryFn: async () => {
      const response = await ordersApi.getOrderHistory();
      return response.data || [];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  } );

  // Filtrar pedidos válidos (que têm orderNumber)
  const validOrders = orders?.filter( order =>
    order &&
    order.orderNumber &&
    order.clientName
  ) || [];

  const isLoading = isLoadingOrders;

  const formatCurrency = ( value: number ) => {
    return new Intl.NumberFormat( "pt-BR", {
      style: "currency",
      currency: "BRL",
    } ).format( value );
  };

  const formatDate = ( dateString: string ) => {
    return new Date( dateString ).toLocaleDateString( "pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    } );
  };

  const handleRegeneratePDF = async ( orderId: string ) => {
    setRegeneratingId( orderId );
    try {
      const order = validOrders?.find( ( o ) => o.id === orderId );
      if ( !order ) {
        toast( {
          title: "Erro",
          description: "Pedido não encontrado",
          variant: "destructive",
        } );
        return;
      }

      await regeneratePDF( order );
    } finally {
      setRegeneratingId( null );
    }
  };

  const handleResendWebhook = async ( orderId: string ) => {
    const now = new Date().getTime();
    const cooldownEndTime = webhookCooldowns[ orderId ];

    if ( cooldownEndTime && cooldownEndTime > now ) {
      const remainingMinutes = Math.ceil( ( cooldownEndTime - now ) / 60000 );
      toast( {
        title: "Ação bloqueada",
        description: `Por favor, aguarde aproximadamente ${ remainingMinutes } minuto(s) antes de tentar reenviar o webhook.`,
        variant: "default",
      } );
      return;
    }

    setResendingWebhookId( orderId );
    try {
      const order = validOrders?.find( ( o ) => o.id === orderId );
      if ( !order || !order.orderNumber ) {
        toast( {
          title: "Erro",
          description: "Pedido não encontrado ou sem número de pedido",
          variant: "destructive",
        } );
        return;
      }

      const response = await ordersApi.resendWebhook( order.orderNumber );

      // Define o cooldown após a tentativa, independentemente do resultado.
      const newCooldownEndTime = new Date().getTime() + 5 * 60 * 1000; // 5 minutos
      const updatedCooldowns = {
        ...webhookCooldowns,
        [ orderId ]: newCooldownEndTime,
      };
      setWebhookCooldowns( updatedCooldowns );
      localStorage.setItem( "webhookCooldowns", JSON.stringify( updatedCooldowns ) );

      if ( response.timeout ) {
        toast( {
          title: "Aviso - Timeout",
          description: response.warning || response.message,
          variant: "default",
        } );
      } else if ( response.success ) {
        toast( {
          title: "Webhook reenviado",
          description: response.message,
        } );
      } else {
        toast( {
          title: "Erro ao reenviar webhook",
          description: response.message,
          variant: "destructive",
        } );
      }
    } catch ( error ) {
      console.error( "Erro ao reenviar webhook:", error );
      toast( {
        title: "Erro ao reenviar webhook",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      } );
    } finally {
      setResendingWebhookId( null );
    }
  };

  const getPortalStatus = ( order: { portalB2BOrderStatus?: string; status?: string } ) => {
    const raw = ( order.portalB2BOrderStatus || order.status || "" ).toLowerCase();
    if ( raw === "pending" || raw === "created" ) return "Criado";
    return order.portalB2BOrderStatus || order.status || "Criado";
  };

  const statusOptions = useMemo(
    () => Array.from( new Set( validOrders.map( ( order ) => getPortalStatus( order ) ) ) ).sort( ( a, b ) => a.localeCompare( b, "pt-BR" ) ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ orders ]
  );

  const filteredOrders = useMemo( () => {
    const term = search.trim().toLowerCase();
    const from = dateFrom ? new Date( `${ dateFrom }T00:00:00` ).getTime() : null;
    const to = dateTo ? new Date( `${ dateTo }T23:59:59.999` ).getTime() : null;

    const result = validOrders.filter( ( order ) => {
      if ( term ) {
        const haystack = `${ order.orderNumber } ${ order.clientName } ${ order.clientCode }`.toLowerCase();
        if ( !haystack.includes( term ) ) return false;
      }
      if ( statusFilter !== "all" && getPortalStatus( order ) !== statusFilter ) return false;
      if ( from !== null || to !== null ) {
        if ( !order.createdAt ) return false;
        const created = new Date( order.createdAt ).getTime();
        if ( from !== null && created < from ) return false;
        if ( to !== null && created > to ) return false;
      }
      return true;
    } );

    const direction = sortDir === "asc" ? 1 : -1;
    return [ ...result ].sort( ( a, b ) => {
      switch ( sortKey ) {
        case "totalAmount":
          return ( ( a.totalAmount ?? 0 ) - ( b.totalAmount ?? 0 ) ) * direction;
        case "createdAt":
          return ( ( a.createdAt ? new Date( a.createdAt ).getTime() : 0 ) - ( b.createdAt ? new Date( b.createdAt ).getTime() : 0 ) ) * direction;
        case "status":
          return getPortalStatus( a ).localeCompare( getPortalStatus( b ), "pt-BR" ) * direction;
        case "clientName":
          return ( a.clientName ?? "" ).localeCompare( b.clientName ?? "", "pt-BR" ) * direction;
        case "clientCode":
          return ( a.clientCode ?? "" ).localeCompare( b.clientCode ?? "", "pt-BR", { numeric: true } ) * direction;
        default:
          return ( a.orderNumber ?? "" ).localeCompare( b.orderNumber ?? "", "pt-BR", { numeric: true } ) * direction;
      }
    } );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ orders, search, statusFilter, dateFrom, dateTo, sortKey, sortDir ] );

  const totalPages = Math.max( 1, Math.ceil( filteredOrders.length / pageSize ) );
  const currentPage = Math.min( page, totalPages );
  const firstIndex = ( currentPage - 1 ) * pageSize;
  const pageOrders = filteredOrders.slice( firstIndex, firstIndex + pageSize );
  const hasFilters = search !== "" || statusFilter !== "all" || dateFrom !== "" || dateTo !== "";

  const clearFilters = () => {
    setSearch( "" );
    setStatusFilter( "all" );
    setDateFrom( "" );
    setDateTo( "" );
    setPage( 1 );
  };

  const toggleSort = ( key: SortKey ) => {
    if ( sortKey === key ) {
      setSortDir( sortDir === "asc" ? "desc" : "asc" );
    } else {
      setSortKey( key );
      // Data e valor começam do maior para o menor; os demais, de A a Z.
      setSortDir( key === "createdAt" || key === "totalAmount" ? "desc" : "asc" );
    }
    setPage( 1 );
  };

  const renderSortHead = ( label: string, key: SortKey, align: "left" | "center" | "right" = "left" ) => {
    const Icon = sortKey === key ? ( sortDir === "asc" ? ArrowUp : ArrowDown ) : ArrowUpDown;
    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
      <TableHead className={align === "right" ? "text-right" : align === "center" ? "text-center" : ""}>
        <button
          type="button"
          onClick={() => toggleSort( key )}
          className={`flex w-full items-center gap-1 whitespace-nowrap font-medium hover:text-foreground ${ justify } ${ sortKey === key ? "text-foreground" : "" }`}
        >
          {label}
          <Icon className={`h-3.5 w-3.5 ${ sortKey === key ? "" : "opacity-40" }`} />
        </button>
      </TableHead>
    );
  };

  return (
    <Layout title="Histórico de Pedidos">
      <div className="space-y-6">
        <Card>
          <CardHeader className="bg-gradient-to-r from-primary to-secondary text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-6 w-6" />
                <CardTitle className="text-xl">
                  Histórico de Pedidos - Todos os Pedidos
                </CardTitle>
              </div>
              <Button
                onClick={() => refetch()}
                disabled={isLoading}
                variant="outline"
                size="sm"
                className="bg-white text-primary hover:bg-gray-100"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${ isLoading ? "animate-spin" : "" }`} />
                Atualizar
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-pulse">Carregando pedidos...</div>
              </div>
            ) : !validOrders || validOrders.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">Nenhum pedido encontrado</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <Input
                      value={search}
                      onChange={( e ) => { setSearch( e.target.value ); setPage( 1 ); }}
                      placeholder="Buscar por número do pedido, cliente ou código"
                      className="pl-9"
                    />
                  </div>
                  <div className="w-full lg:w-[190px]">
                    <Select value={statusFilter} onValueChange={( value ) => { setStatusFilter( value ); setPage( 1 ); }}>
                      <SelectTrigger aria-label="Filtrar por status">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos os status</SelectItem>
                        {statusOptions.map( ( status ) => (
                          <SelectItem key={status} value={status}>{status}</SelectItem>
                        ) )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={dateFrom}
                      max={dateTo || undefined}
                      onChange={( e ) => { setDateFrom( e.target.value ); setPage( 1 ); }}
                      aria-label="Data inicial"
                      className="w-full lg:w-[150px]"
                    />
                    <span className="text-sm text-gray-500">até</span>
                    <Input
                      type="date"
                      value={dateTo}
                      min={dateFrom || undefined}
                      onChange={( e ) => { setDateTo( e.target.value ); setPage( 1 ); }}
                      aria-label="Data final"
                      className="w-full lg:w-[150px]"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    disabled={!hasFilters}
                    className="gap-1"
                  >
                    <X className="h-4 w-4" />
                    Limpar
                  </Button>
                </div>

                <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      {renderSortHead( "Número do Pedido", "orderNumber" )}
                      {renderSortHead( "Cliente", "clientName" )}
                      {renderSortHead( "Código Cliente", "clientCode" )}
                      {renderSortHead( "Valor Total", "totalAmount", "right" )}
                      {renderSortHead( "Status", "status", "center" )}
                      {renderSortHead( "Data", "createdAt", "center" )}
                      <TableHead className="text-center">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageOrders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="py-8 text-center text-gray-600">
                          Nenhum pedido encontrado com os filtros informados.
                        </TableCell>
                      </TableRow>
                    )}
                    {pageOrders.map( ( order ) => {
                      const isWebhookOnCooldown =
                        webhookCooldowns[ order.id! ] &&
                        webhookCooldowns[ order.id! ] > new Date().getTime();

                      return (
                        <TableRow key={order.id} className="hover:bg-gray-50">
                          <TableCell className="whitespace-nowrap font-medium">
                            #{order.orderNumber}
                          </TableCell>
                          <TableCell>{order.clientName}</TableCell>
                          <TableCell className="whitespace-nowrap">{order.clientCode}</TableCell>
                          <TableCell className="whitespace-nowrap text-right font-medium">
                            {formatCurrency( order.totalAmount )}
                          </TableCell>
                          <TableCell className="text-center">
                            <StateBadge status={getPortalStatus( order )} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-center text-sm text-gray-600">
                            {order.createdAt ? formatDate( order.createdAt ) : "-"}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-end gap-2">
                              <div className={ACTION_SLOT}>
                                <Button
                                  onClick={() => handleRegeneratePDF( order.id! )}
                                  disabled={regeneratingId === order.id}
                                  variant="outline"
                                  size="sm"
                                  className="w-full gap-2"
                                >
                                  {regeneratingId === order.id ? (
                                    <>
                                      <RefreshCw className="h-4 w-4 animate-spin" />
                                      Gerando...
                                    </>
                                  ) : (
                                    <>
                                      <Download className="h-4 w-4" />
                                      Gerar PDF
                                    </>
                                  )}
                                </Button>
                              </div>
                              {isErpDirectIntegrationEnabled && (
                                <div className={ACTION_SLOT}>
                                  <Button
                                    onClick={() =>
                                      setIntegrationOrder( { id: order.id!, orderNumber: order.orderNumber! } )
                                    }
                                    variant="outline"
                                    size="sm"
                                    className="w-full gap-2"
                                  >
                                    <Activity className="h-4 w-4" />
                                    Integração
                                  </Button>
                                </div>
                              )}
                              <div className={ACTION_SLOT}>
                                {getPortalStatus( order ) === "Criado" && (
                                  <TooltipProvider>
                                    <Tooltip delayDuration={200}>
                                      <TooltipTrigger asChild>
                                        <div className="relative">
                                          <Button
                                            onClick={() =>
                                              handleResendWebhook( order.id! )
                                            }
                                            disabled={
                                              resendingWebhookId === order.id ||
                                              isWebhookOnCooldown
                                            }
                                            variant="outline"
                                            size="sm"
                                            className="w-full gap-2"
                                          >
                                            {resendingWebhookId === order.id ? (
                                              <>
                                                <RefreshCw className="h-4 w-4 animate-spin" />
                                                Enviando...
                                              </>
                                            ) : (
                                              <>
                                                <Send className="h-4 w-4" />
                                                Webhook
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                      </TooltipTrigger>
                                      {isWebhookOnCooldown && (
                                        <TooltipContent>
                                          <p>Aguarde para reenviar.</p>
                                        </TooltipContent>
                                      )}
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    } )}
                  </TableBody>
                </Table>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-600">
                    {filteredOrders.length === 0
                      ? "0 pedidos"
                      : `Mostrando ${ firstIndex + 1 }–${ Math.min( firstIndex + pageSize, filteredOrders.length ) } de ${ filteredOrders.length } pedido(s)`}
                    {hasFilters && ` (${ validOrders.length } no total)`}
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Por página</span>
                      <Select
                        value={String( pageSize )}
                        onValueChange={( value ) => { setPageSize( Number( value ) ); setPage( 1 ); }}
                      >
                        <SelectTrigger className="h-8 w-[78px]" aria-label="Pedidos por página">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGE_SIZES.map( ( size ) => (
                            <SelectItem key={size} value={String( size )}>{size}</SelectItem>
                          ) )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage( 1 )} disabled={currentPage === 1} aria-label="Primeira página">
                        <ChevronsLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage( currentPage - 1 )} disabled={currentPage === 1} aria-label="Página anterior">
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="min-w-[96px] text-center text-sm text-gray-700">
                        Página {currentPage} de {totalPages}
                      </span>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage( currentPage + 1 )} disabled={currentPage === totalPages} aria-label="Próxima página">
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage( totalPages )} disabled={currentPage === totalPages} aria-label="Última página">
                        <ChevronsRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-1">Sobre as ações disponíveis</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    <strong>Gerar PDF:</strong> Recria o documento do pedido com base nos dados salvos
                    no banco de dados. O arquivo será baixado automaticamente com o sufixo "_2aVia" no nome.
                  </li>
                  <li>
                    <strong>Webhook:</strong> Reenvia o webhook do pedido para o sistema Magento.
                    Útil para processar novamente pedidos que falharam no envio inicial.
                  </li>
                  {isErpDirectIntegrationEnabled && (
                    <li>
                      <strong>Integração:</strong> Mostra o andamento do envio do pedido ao ERP (passo a passo, JSON
                      enviado e resposta). Com o envio direto ligado, o reenvio também vai direto ao ERP.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {isErpDirectIntegrationEnabled && (
        <ErpIntegrationSheet order={integrationOrder} onClose={() => setIntegrationOrder( null )} />
      )}
    </Layout>
  );
}
